/**
 * Integrytas Group - UI Layout Delta Override Engine (ULD)
 * uld_describe - turns stored delta actions into short, human-readable lines for
 * display (e.g. the About window's per-tier personalization breakdown). Pure +
 * presentation-only; no i18n keys (the field/column keys themselves are data).
 */

import type { UldDeltaAction } from './uld_merge';

/** A single human-readable personalization change, tagged with its component. */
export interface UldChangeLine {
  /** The component the change belongs to ('grid', 'drawer', …; '' = the page form). */
  component_key: string;
  /** Plain-English description of the change. */
  text: string;
}

/** Render one delta action as a short, human-readable sentence fragment. */
export function describeDeltaAction(a: UldDeltaAction): string {
  const key = a.component_key || 'field';
  switch (a.action) {
    case 'hide_element':       return `${key} hidden`;
    case 'set_visible':        return a.visible === false ? `${key} hidden` : `${key} shown`;
    case 'set_readonly':       return a.readonly ? `${key} made read-only` : `${key} made editable`;
    case 'set_required':       return a.required ? `${key} made required` : `${key} made optional`;
    case 'reorder_index':      return `${key} moved to position ${(a.index ?? 0) + 1}`;
    case 'set_col_width':      return `${key} width set to ${a.width}px`;
    case 'set_col_span':       return `${key} column span set to ${a.col_span}`;
    case 'relabel_component':  return `${key} relabelled${a.label ? ` to “${a.label}”` : ''}`;
    case 'assign_field_to_tab':return `${key} moved to tab${a.tab ? ` “${a.tab}”` : ''}`;
    case 'set_section':        return `${key} moved to section${a.section ? ` “${a.section}”` : ''}`;
    case 'reorder_section':    return `section ${key} moved to position ${(a.index ?? 0) + 1}`;
    default:                   return `${key} changed`;
  }
}

/** Flatten a tier's delta map ({ component: actions[] }) into readable lines. */
export function describeDeltaMap(map: Record<string, UldDeltaAction[]> | null | undefined): UldChangeLine[] {
  if (!map || typeof map !== 'object') return [];
  const lines: UldChangeLine[] = [];
  for (const [component_key, actions] of Object.entries(map)) {
    for (const a of actions ?? []) lines.push({ component_key, text: describeDeltaAction(a) });
  }
  return lines;
}
